import InnerNav from "../../../components/InnerNav/InnerNav";
import Zodiac from "../../../components/ZodiacSign/Zodiac";
import "./Chinese.scss";
import arrow from "../../../assets/arrow.png";
import Footer from "../../../components/Footer/Footer";
import { Link } from "react-router-dom";
import axios from "axios";
import { useState } from "react";

// -------------------------------------------------------
// CHINESE LUCKY NUMBER PROFILES (Professional Style)
// -------------------------------------------------------
export const chineseProfiles = {
  2: {
    title: "Number 2 – Harmony & Partnership",
    keywords: "Balance · Pairs · Unity",
    overview:
      "In Chinese numerology, Number 2 symbolizes harmony and partnership. Chinese culture believes that 'good things come in pairs'.",
    strengths: [
      "Brings unity and cooperation",
      "Creates balance in relationships",
      "Considered auspicious during weddings and celebrations",
    ],
    challenges: [
      "Can depend too much on others for stability",
      "Sometimes avoids necessary conflicts",
    ],
    guidance:
      "Use the energy of pairs wisely. Work with supportive partners and avoid imbalance in relationships.",
  },
  3: {
    title: "Number 3 – Growth & Birth",
    keywords: "Creation · Life · Positivity",
    overview:
      "Number 3 is considered lucky because it sounds similar to the Chinese word for 'birth'. It represents growth and vitality.",
    strengths: [
      "Symbolizes new beginnings",
      "Brings creativity and expression",
      "Encourages positive movement in life",
    ],
    challenges: [
      "May bring too much change at once",
      "Can be overwhelming without direction",
    ],
    guidance:
      "Embrace growth but stay grounded while moving into new phases of life.",
  },
  6: {
    title: "Number 6 – Smooth Success",
    keywords: "Flow · Blessings · Happiness",
    overview:
      "Number 6 (pronounced 'liu') means 'everything goes smoothly'. It represents blessings, happiness, and effortless progress.",
    strengths: [
      "Auspicious in business and career",
      "Encourages financial flow and stability",
      "Brings ease in difficult situations",
    ],
    challenges: [
      "May create overconfidence",
      "Progress may slow without effort",
    ],
    guidance:
      "Use your natural flow but combine it with discipline for long-term success.",
  },
  8: {
    title: "Number 8 – Wealth & Prosperity",
    keywords: "Fortune · Abundance · Success",
    overview:
      "Number 8 is the luckiest number in Chinese culture. Its pronunciation sounds like 'prosperity'.",
    strengths: [
      "Attracts money and business success",
      "Represents achievement and authority",
      "Extremely powerful for entrepreneurs",
    ],
    challenges: [
      "Can lead to excessive material focus",
      "High expectations may cause pressure",
    ],
    guidance:
      "Use this powerful number ethically. Combine ambition with wisdom.",
  },
  9: {
    title: "Number 9 – Eternity & Completion",
    keywords: "Longevity · Fulfillment · Strength",
    overview:
      "Number 9 represents long-lasting love, eternity, and spiritual completion. Common in weddings and ceremonies.",
    strengths: [
      "Brings emotional depth and longevity",
      "Symbolizes completeness and wisdom",
      "Excellent for relationships",
    ],
    challenges: [
      "May hold onto the past too long",
      "Can lead to emotional heaviness",
    ],
    guidance:
      "Embrace the eternal nature of 9 but learn to let go when needed.",
  },
  13: {
    title: "Number 13 – ‘Definitely Living’",
    keywords: "Hope · Strength · Life",
    overview:
      "Although unlucky in the West, Number 13 is lucky in China because it sounds like ‘definitely living’.",
    strengths: [
      "Represents resilience",
      "Brings positive transformation",
      "Symbolizes new chances in life",
    ],
    challenges: [
      "Often misunderstood culturally",
      "Can invite drastic life shifts",
    ],
    guidance:
      "Use this number confidently—its meaning is deeply positive in Asia.",
  },
};

const Chinese = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    year: "2000",
    month: "May",
    day: "26",
    gender: "male" // ✅ DEFAULT, NO UI
  });

  const [resultData, setResultData] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [loading, setLoading] = useState(false);

  // Handle Change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    const { firstName, lastName, year, month, day } = formData;
    const birthDay = Number(day);
    const birthMonth = new Date(`${month} 1, 2000`).getMonth() + 1;
    const birthYear = Number(year);

    try {
      setLoading(true);
      const { gender } = formData;

      const res = await axios.get(
        `https://core-c343.onrender.com/api/lucky-number` +
        `?firstName=${firstName}` +
        `&lastName=${lastName}` +
        `&birthYear=${birthYear}` +
        `&birthMonth=${birthMonth}` +
        `&birthDay=${birthDay}` +
        `&gender=${gender}`
      );

      setResultData(res.data);
      setShowReport(true);
    } catch (err) {
      setResultData({
        error: "Could not compute your Chinese Lucky Number. Try again.",
      });
      setShowReport(true);
    } finally {
      setLoading(false);
    }
  };

  const handleTryAgain = () => {
    setShowReport(false);
    setResultData(null);
  };

  const chineseLucky = resultData?.data?.numerology?.chineseLuckyNumber;
  const profile = chineseProfiles[chineseLucky];

  return (
    <div className="Chinese-container">
      <InnerNav />

      <div className="Chinese-content">
        <Zodiac />

        <div className="left-Chinese">

          {/* ----------------------------------------------------------
                 FORM VIEW (Same style as Lucky Number)
          ---------------------------------------------------------- */}
          {!showReport && (
            <>
              <div className="header">
                <h1>Chinese Lucky Number Calculator</h1>
                <p>Find your Chinese Lucky Number and understand its deeper meaning.</p>
              </div>

              <div className="card1">
                <form className="card-content" onSubmit={handleSubmit}>
                  <input
                    name="firstName"
                    placeholder="First Name"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                  <input
                    name="middleName"
                    placeholder="Middle Name (Optional)"
                    value={formData.middleName}
                    onChange={handleChange}
                  />
                  <input
                    name="lastName"
                    placeholder="Last Name"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />

                  {/* DOB */}
                  <select name="year" value={formData.year} onChange={handleChange}>
                    {Array.from({ length: 110 }).map((_, i) => {
                      const y = 2025 - i;
                      return <option key={y}>{y}</option>;
                    })}
                  </select>

                  <select name="month" value={formData.month} onChange={handleChange}>
                    {[
                      "January",
                      "February",
                      "March",
                      "April",
                      "May",
                      "June",
                      "July",
                      "August",
                      "September",
                      "October",
                      "November",
                      "December",
                    ].map((m) => (
                      <option key={m}>{m}</option>
                    ))}
                  </select>

                  <select name="day" value={formData.day} onChange={handleChange}>
                    {[...Array(31)].map((_, i) => (
                      <option key={i + 1}>{i + 1}</option>
                    ))}
                  </select>
                  <select
  name="gender"
  value={formData.gender}
  onChange={handleChange}
  required
>
  <option value="male">Male</option>
  <option value="female">Female</option>
  <option value="other">Other</option>
</select>


                  <button type="submit" disabled={loading}>
                    {loading ? "Calculating..." : "Show My Chinese Lucky Number"}
                  </button>
                </form>
              </div>
            </>
          )}

          {/* ----------------------------------------------------------
                REPORT VIEW (IDENTICAL TO LUCKY NUMBER FORMAT)
          ---------------------------------------------------------- */}
          {showReport && (
            <div className="report-card">
              <h1>Your Chinese Lucky Number Report</h1>

              {resultData?.error ? (
                <p className="error-text">{resultData.error}</p>
              ) : (
                <>
                  {/* HERO SECTION */}
                  <div className="hero-number-box">
                    <div className="number-circle">{chineseLucky}</div>
                    <h2>{profile?.title}</h2>
                    <p className="keywords">{profile?.keywords}</p>
                  </div>

                  {/* Basic Details */}
                  <div className="report-section">
                    <h3>Your Details</h3>
                    <p>
                      <strong>Name:</strong> {formData.firstName} {formData.middleName}{" "}
                      {formData.lastName}
                    </p>
                    <p>
                      <strong>Date of Birth:</strong> {formData.day} {formData.month}{" "}
                      {formData.year}
                    </p>
                  </div>

                  {/* Overview */}
                  <div className="report-section">
                    <h3>Meaning & Overview</h3>
                    <p>{profile?.overview}</p>
                  </div>

                  {/* Strengths */}
                  <div className="report-section">
                    <h3>Strengths</h3>
                    <ul>
                      {profile?.strengths.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Challenges */}
                  <div className="report-section">
                    <h3>Challenges</h3>
                    <ul>
                      {profile?.challenges.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Guidance */}
                  <div className="report-section">
                    <h3>Guidance</h3>
                    <p>{profile?.guidance}</p>
                  </div>
                </>
              )}

              {/* Try Again Button */}
              <button className="try-again-btn" onClick={handleTryAgain}>
                Try Again
              </button>

              {/* More Tools */}
              {/* <div className="additional-tools">
                <h4>Explore More Numerology Tools</h4>
                <ul>
                  <li>
                    <Link to="/numerology/luckynum">Lucky Number</Link>
                  </li>
                  <li>
                    <Link to="/numerology/life-path">Life Path Number</Link>
                  </li>
                  <li>
                    <Link to="/numerology/destiny">Destiny Number</Link>
                  </li>
                  <li>
                    <Link to="/numerology/unlucky">Unlucky Number</Link>
                  </li>
                </ul>
              </div> */}
            </div>
          )}
        </div>

        {/* RIGHT SIDEBAR */}
        <div className="right-numerology">
          <h4>Related Links</h4>

          <div className="right-links">
            <Link to="/numerology/luckynum"><p><img src={arrow}/> Lucky Number</p></Link>
            <Link to="/numerology/chinese-lucky"><p><img src={arrow}/> Chinese Lucky</p></Link>
            <Link to="/numerology/unlucky"><p><img src={arrow}/> Unlucky Number</p></Link>
            <Link to="/numerology/life-path"><p><img src={arrow}/> Life Path</p></Link>
            <Link to="/numerology/destiny"><p><img src={arrow}/> Destiny Number</p></Link>
            <Link to="/numerology/birth"><p><img src={arrow}/> Birth or Root Number</p></Link>
            <Link to="/numerology/soul"><p><img src={arrow}/> Soul Urge Number</p></Link>
            <Link to="/numerology/name"><p><img src={arrow}/> Name Number</p></Link>
            <Link to="/numerology/balance"><p><img src={arrow}/> Balance Number</p></Link>
            <Link to="/numerology/lo-shu-grid"><p><img src={arrow}/> Lo Shu Grid Analysis</p></Link>
            <Link to="/numerology/kua"><p><img src={arrow}/> Kua Number</p></Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Chinese;
